import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, mergeMap, switchMap, tap } from 'rxjs/operators';
import * as fromAction from './template.action';
import { PageList } from '@core/models/page-list';
import { Template } from '@modules/template/student/models/template';
import { ClassroomCourse } from '@modules/course/classroom/models';
import { TemplateApiService } from '@modules/template/student/store/services/template-api.service';
import { Router } from "@angular/router";
import { templateFeatureKey } from './template.state';
import { EffectHelperService } from '@modules/core/services/effect-helper.service';

@Injectable()
export class TemplateEffects {
  $fetchTemplates = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAction.FetchTemplates),
      switchMap(({ filter }) => {
        return this.templateApi.fetchTemplates(filter).pipe(
          map((template: PageList<Template>) =>
          this.effectHelperService.handleSuccess(fromAction.AddTemplates({ templates: template.items }))),
          this.effectHelperService.handleError(fromAction.AddError, true, templateFeatureKey)
        );
      })
    )
  );

  $fetchTemplate = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAction.FetchTemplate),
      switchMap(({ id }) => {
        return this.templateApi.fetchTemplate(id).pipe(
          map((template: Template) =>
          this.effectHelperService.handleSuccess(fromAction.AddTemplate({ template }))),
          this.effectHelperService.handleError(fromAction.AddError, true, templateFeatureKey)
        );
      })
    )
  );

  $fetchClassroomCourses = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAction.FetchClassroomCourses),
      switchMap(({ templateId, filter }) => {
        return this.templateApi.fetchTemplateClassroom(templateId, filter).pipe(
          map((classroom: PageList<ClassroomCourse>) =>
          this.effectHelperService.handleSuccess(fromAction.AddClassroomCourses({ classrooms: classroom.items }))),
          this.effectHelperService.handleError(fromAction.AddError, true, templateFeatureKey)
        );
      })
    )
  );

  $fetchAllTemplates = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAction.FetchAllTemplates),
      switchMap(({ filter }) => {
        return this.templateApi.fetchTemplates(filter).pipe(
          map(({ items, total}) =>
          this.effectHelperService.handleSuccess(fromAction.FetchTemplatesSlotCount({ templates: items }))),
          this.effectHelperService.handleError(fromAction.AddError, true, templateFeatureKey)
        );
      })
    )
  );

  $fetchTemplateSlotCount = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAction.FetchTemplatesSlotCount),
      mergeMap(({ templates }) => {
        return this.templateApi.fetchTemplatesSlotCount().pipe(
          map((templatesSlotCount) => {
            const templateList = templates.map(template => {
              return ({ ...template, availableSlots: templatesSlotCount.hasOwnProperty(template.id) ? templatesSlotCount[template.id] : 0 })
            });
            return this.effectHelperService.handleSuccess(fromAction.AddTemplates({ templates: templateList }))
          }),
          this.effectHelperService.handleError(fromAction.AddError, false, templateFeatureKey)
        );
      })
    )
  );

  requestClassroomCourse$ = createEffect(() =>
      this.actions$.pipe(
        ofType(fromAction.RequestClassroomCourse),
        mergeMap(({ courseRequest }) => {
          return this.templateApi.requestCourse(courseRequest).pipe(
            tap(() => {
              this.router.navigate(['/alle-kurs']);
              return this.effectHelperService.handleSuccess(null, true, 'course_request_success', templateFeatureKey)
            }),
            this.effectHelperService.handleError(fromAction.AddError, true, templateFeatureKey)
          )
        })
      ),{
      dispatch: false
    }
  );

  constructor(private actions$: Actions,
              private templateApi: TemplateApiService,
              private router: Router,
              private effectHelperService: EffectHelperService
  ) {}
}
